/**
 * RWBR — THE DRAFT PHASE, inside a turn.
 *
 * EZRA-S2 §3.10 is emphatic about WHEN this happens:
 *
 *   "The DS makes his challenge attempt AFTER all other challenges have been
 *    completed."
 *
 * So drafting is not a modifier bolted onto the normal challenge sequence — it
 * is a phase that runs after it, against the running order the challenges left
 * behind. That ordering is the reason this lives in its own module rather than
 * inside `challenge.ts`: putting it there would mean interleaving the DS
 * driver's attempt with everyone else's, which is the one thing EZRA forbids.
 *
 * WHAT A DS ATTEMPT IS. A gas challenge, the ordinary kind, with one point off
 * the red die (`draftRedDie`). Red must come in at or under his gas pedal, so
 * the −1 makes him likelier to get it done — and on success the Draft Event
 * fires and tows the car behind him through as well.
 *
 * WHAT CANCELS IT. Anything that interrupted the turn before this point: a
 * caution, a PROBLEM, a TROUBLE result. EZRA: "Any interruption of the turn
 * prior to the DE due to RWBR or EZRA procedure will cancel the DE." The caller
 * passes `interrupted` and the whole phase is skipped — no roll is made, which
 * matters, because a roll made and discarded still burns a die and would
 * desynchronise a seeded replay.
 */

import type { Dice } from './dice.ts';
import type { Car, Quality, RaceState } from './types.ts';
import type { RuleEvent } from './ruleEvent.ts';
import type { TrackCard } from './turn.ts';
import {
  CITE_DRAFT_ORDER,
  CITE_DRAFT_RED_DIE,
  applyDraftEvent,
  canDraftWhileLapped,
  describeDraft,
  draftEligible,
  draftEvent,
  draftRedDie,
  hasDS,
} from './drafting.ts';

export const DRAFT_ROLL = 'draft gas challenge (red)';
const CITE_PACK = 'House rule D-EZ-84 — superspeedway pack racing (EZRA-S2 §3.10 Draft Event for every car)';

export interface DraftPhaseInput {
  state: RaceState;
  /** The running order AFTER the ordinary challenges have resolved. */
  order: readonly Car[];
  track: Pick<TrackCard, 'category'>;
  /** The quality the RACE CAM called this turn. */
  quality: Quality;
  dice: Dice;
  /** Did anything interrupt the turn before this phase? */
  interrupted: boolean;
  /**
   * The group the RACE CAM actually called to challenge this turn.
   *
   * REQUIRED, and the most important gate in the module. EZRA triggers a draft
   * "on any quality challenge action **when the DS driver is the challenger**",
   * and only the called group challenges on a given turn. Without this filter
   * every DS driver on the board attempts a draft every single turn —
   * measured at 388% of turns before it was added, which reads as "drafting is
   * exciting" rather than "drafting is broken".
   */
  calledGroup: 'top' | 'middle' | 'bottom';
  /** First index of the BOTTOM group, so a draft cannot climb out of it. */
  bottomGroupFrom?: number;
  /**
   * HOUSE RULE D-EZ-84 — SUPERSPEEDWAY PACK RACING. Every running, lead-lap car
   * in the called group drafts as if it carried the DS trait: the same EZRA-S2
   * §3.10 Draft Event (a gas check with one off the red die; on success he takes
   * the car ahead and tows the next car through). Only the TRAIT gate is lifted
   * — every other gate (superspeedway only, uninterrupted turn, the called group,
   * lead lap) stands, and the three-car rule still needs two real DS drivers.
   * Absent / false = the printed rule: DS-trait drivers only.
   */
  everyoneDrafts?: boolean;
}

/**
 * Positions belonging to each called group (RB/p.i).
 * LEADER is 1; TOP is the six behind him; MIDDLE the next twelve; BOTTOM the
 * rest. The leader never challenges — there is nobody ahead of him.
 */
export function inCalledGroup(position: number, group: 'top' | 'middle' | 'bottom'): boolean {
  if (group === 'top') return position >= 2 && position <= 7;
  if (group === 'middle') return position >= 8 && position <= 19;
  return position >= 20;
}

export interface DraftPhaseResult {
  order: Car[];
  events: RuleEvent[];
}

/**
 * Run the draft phase.
 *
 * Returns the order unchanged and no events when drafting cannot apply, which
 * is the overwhelmingly common case: it needs a super-speedway, a DS driver, an
 * uninterrupted turn, and a won challenge. On every other track this function
 * makes no rolls at all and costs nothing.
 */
export function runDraftPhase(input: DraftPhaseInput): DraftPhaseResult {
  const { track, quality, dice, interrupted } = input;
  let order = [...input.order];
  const events: RuleEvent[] = [];

  // The cheapest gate first, and it is the one that is almost always false.
  // Checked before any driver is examined so a Martinsville turn does no work.
  const gate = draftEligible({
    track,
    challenger: { trait: 'DS' },
    isChallenger: true,
    turnInterrupted: interrupted,
  });
  if (!gate.eligible) return { order, events };

  // Only DS drivers IN THE CALLED GROUP were challengers this turn, and only a
  // challenger can draft. They attempt in running order, front to back, each
  // seeing the order the previous one left — so two drafts in a turn compose
  // rather than both resolving against a stale grid.
  const everyone = input.everyoneDrafts === true;
  const candidates = order
    .filter(
      (c) =>
        (everyone ? !c.cannotRace : hasDS(c.card)) &&
        c.status === 'running' &&
        canDraftWhileLapped(c) &&
        inCalledGroup(c.position, input.calledGroup),
    )
    .map((c) => c.card.driverId);

  for (const driverId of candidates) {
    const index = order.findIndex((c) => c.card.driverId === driverId);
    // A DS driver at the front has nobody to challenge; a draft needs a target
    // ahead of him.
    if (index <= 0) continue;

    const car = order[index];
    const target = order[index - 1];

    const raw = dice.about([driverId, target.card.driverId], () => dice.d6('red'));
    const red = draftRedDie(raw);
    const passed = red <= car.card.gasPedal;

    const dieRecord = {
      kind: 'd6' as const,
      dice: [raw],
      label: DRAFT_ROLL,
      faces: { red: raw },
      total: red,
    };

    if (!passed) {
      events.push({
        eventType: 'draft',
        momentTag: 'draft.missed',
        ruleCitation: hasDS(car.card) ? CITE_DRAFT_RED_DIE : `${CITE_DRAFT_RED_DIE} · ${CITE_PACK}`,
        actorIds: [driverId],
        positionDeltas: [],
        modifiers: [
          { name: 'DS red die', value: -1 },
          { name: 'gas pedal', value: car.card.gasPedal },
          ...(hasDS(car.card) ? [] : [{ name: 'pack racing', value: 'every car drafts', detail: CITE_PACK }]),
        ],
        dice: [dieRecord],
        description: `${car.card.name} looks for the draft on ${target.card.name} and cannot get the run — red ${raw} less one is ${red}, outside ${car.card.gasPedal} gas.`,
      });
      continue;
    }

    const de = draftEvent({
      grid: order,
      dsDriverId: driverId,
      targetDriverId: target.card.driverId,
      bottomGroupFrom: input.bottomGroupFrom,
    });
    if (!de) continue;

    const before = new Map(order.map((c) => [c.card.driverId, c.position]));
    order = applyDraftEvent(order, de);

    const deltas = order
      .filter((c) => before.get(c.card.driverId) !== c.position)
      .map((c) => ({
        driverId: c.card.driverId,
        from: before.get(c.card.driverId) as number,
        to: c.position,
      }));

    events.push({
      eventType: 'draft',
      momentTag: de.secondDsDriverId ? 'draft.event.double' : 'draft.event',
      ruleCitation: `${CITE_DRAFT_ORDER} · ${de.ruleCitation}${hasDS(car.card) ? "" : ` · ${CITE_PACK}`}`,
      actorIds: [
        de.dsDriverId,
        de.targetDriverId,
        ...(de.dmdDriverId ? [de.dmdDriverId] : []),
        ...(de.extraDmdDriverId ? [de.extraDmdDriverId] : []),
      ],
      positionDeltas: deltas,
      modifiers: [
        { name: 'DS red die', value: -1 },
        { name: 'gas pedal', value: car.card.gasPedal },
        { name: 'cars in the draft', value: de.carsMoved },
        ...(hasDS(car.card) ? [] : [{ name: 'pack racing', value: 'every car drafts', detail: CITE_PACK }]),
      ],
      dice: [dieRecord],
      description: describeDraft(
        de,
        (id) => order.find((c) => c.card.driverId === id)?.card.name ?? id,
        quality,
      ),
    });
  }

  return { order, events };
}

/** Is there any point running the phase at all? Used to skip it cheaply. */
export const anyDrafters = (state: Pick<RaceState, 'grid'>, track: Pick<TrackCard, 'category'>): boolean =>
  draftEligible({ track, challenger: { trait: 'DS' }, isChallenger: true }).eligible &&
  state.grid.some((c) => hasDS(c.card) && c.status === 'running');
